import React, { FC, useState } from 'react'

import Select from 'react-select'
import { Loader2Icon, MessageCircle, PlusIcon, RefreshCcw } from 'lucide-react'
import { Link } from 'react-router-dom'

import { IconSearch } from '../../../components/icons/IconSearch'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip'
import { Button } from '../../../components/ui/button'

import { formatOptionLabel } from '../../../helpers/select'
import { getConvertedData } from '../../../models/interface.helper'
import { getUserFullName } from '../../../helpers/helpers'
import { useChatClient } from '../../messages/components/withStreamClient'

import FirestoreService from '../../../services/firestoreService'

import { useAppSelector } from '../../../store/hooks'
import { selectProfileData } from '../../../store/user/userSlice'

import { IUserInterface } from '../../../models/users/user.interface'
import { UserModel } from '../../../models/users/user.model'
import { IRider } from '../../../types/entryRider'
import { ALL_CLASSES, ALL_STATUSES, EStatuses } from './hooks/useManageClinicNOtherAdminTabNew'

import { customSelectStyles } from '../../../customStyles/ReactSelectCustomStyle'

import { CONST } from '../../../const/const'

const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

const statuses = [
  ALL_STATUSES,
  { value: EStatuses.noIssues, label: EStatuses.noIssues },
  { value: EStatuses.issues, label: EStatuses.issues },
  { value: EStatuses.scratched, label: EStatuses.scratched },
  { value: EStatuses.draft, label: EStatuses.draft },
]

interface ManageEntriesActionsProps {
  activeStatus: { value: string; label: string } | null
  setActiveStatus: (value: { value: string; label: string }) => void
  filteredRiders: IRider[]
  allRiders: IRider[]
  setSearchNameValue: (value: string) => void
  setActiveClass: (value: { value: string; label: string }) => void
  activeClass: { value: string; label: string }
  searchNameValue: string
  ridersClasses: { value: string; label: string }[]
  eventId: string
  syncLoading: boolean
}

export const ManageEntriesActions: FC<ManageEntriesActionsProps> = ({
  activeStatus,
  setActiveStatus,
  filteredRiders,
  allRiders,
  activeClass,
  setActiveClass,
  searchNameValue,
  setSearchNameValue,
  ridersClasses,
  eventId,
  syncLoading,
}) => {
  const { createChatEvent } = useChatClient()

  const profileData = useAppSelector(selectProfileData)

  const [messageLoading, setMessageLoading] = useState(false)

  const statusSelectHandler = (itm: typeof ALL_STATUSES) => {
    setActiveStatus(itm)
    const totalClassesCount = allRiders.reduce((acc, item) => acc + item.classes.length, 0)

    setActiveClass({
      value: ALL_CLASSES.value,
      label: `${ALL_CLASSES.label}  <span class="text-[#122B4680]">• ${totalClassesCount}  entries</span>`,
    })

    setSearchNameValue('')
  }

  const selectClassHandler = (itm: typeof ALL_CLASSES) => {
    setActiveClass(itm)
    setActiveStatus(ALL_STATUSES)
    setSearchNameValue('')
  }

  const onchangeSearchRiderName = (value: string) => {
    setSearchNameValue(value)

    if (activeClass.value !== ALL_CLASSES.value) {
      setActiveClass({
        value: ALL_CLASSES.value,
        label: `${ALL_CLASSES.label}  <span class="text-[#122B4680]">• ${allRiders.length}  entries</span>`,
      })
    }

    if (activeStatus?.value !== ALL_STATUSES.value && activeStatus?.value) {
      setActiveStatus(ALL_STATUSES)
    }
  }

  const onMessageAll = async () => {
    setMessageLoading(true)
    const trainersIds: string[] = []

    filteredRiders.forEach((rider) => {
      if (rider.horsesSelected && rider.horsesSelected?.length > 0 && rider.riderId) {
        trainersIds.push(rider.riderId)
      }
    })
    const usersList = [...new Set(trainersIds)]

    let usersSnaps = await FirestoreService.getItemsUsingIds(COLLECTIONS.USERS.NAME, usersList)
    const usersData: IUserInterface[] = []
    usersSnaps.forEach((currSnap) => {
      usersData.push(getConvertedData(UserModel.fromFirestoreDoc(currSnap).toObject()))
    })

    const users = usersData.map((user) => ({
      ...user,
      name: getUserFullName(user),
      role: 'Rider',
      vertical: 'exhibitors',
    }))
    await createChatEvent(users, eventId, { eventId: eventId }).then(() => {
      const url = `${CONST.ROUTES.MANAGE.CLINIC_N_OTHER.URL}/${eventId}/messages`
      window.open(url, '_blank')
    })
    setMessageLoading(true)
  }

  return (
    <div className="flex flex-wrap flex-[1_0_auto] flex-col md:flex-row gap-2.5 justify-end max-w-full">
      <div className="w-full md:w-[200px] flex justify-end">
        <div className="relative w-full md:w-auto inline-flex rounded-lg border border-SeabiscuitGray500ThemeColor hover:border-SeabiscuitGray600ThemeColor transition-all">
          <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
            <IconSearch />
          </div>
          <input
            type="search"
            id="default-search"
            className="block w-full md:w-[41px] md:hover:w-[200px] md:[&:not(:placeholder-shown)]:w-[200px] bg-transparent p-0 h-[42px] pl-10 text-nr border-0 text-SeabiscuitDark200ThemeColor focus:ring-0 focus:outline-none rounded-lg placeholder:text-nr transition-all"
            placeholder="Search name"
            value={searchNameValue}
            onChange={(event) => onchangeSearchRiderName(event.target.value)}
            onFocus={() => null}
            onBlur={() => null}
          />
        </div>
      </div>
      <Select
        value={activeClass}
        defaultValue={activeClass}
        options={ridersClasses}
        formatOptionLabel={formatOptionLabel}
        styles={customSelectStyles}
        className="w-full flex-1 md:max-w-[450px] sm:min-w-[250px]"
        onChange={(selectedOption) => selectClassHandler(selectedOption as typeof ALL_CLASSES)}
      />
      <Select
        placeholder="Sort"
        menuShouldScrollIntoView
        value={activeStatus}
        options={statuses}
        className="w-full md:w-[140px]"
        styles={customSelectStyles}
        onChange={(selectedOption: any) => statusSelectHandler(selectedOption)}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            disabled={!filteredRiders?.[0]}
            variant="outline"
            className="text-black/50 w-[45px] h-[42px] rounded-md"
            onClick={onMessageAll}
          >
            {messageLoading ? (
              <Loader2Icon className="animate-spin w-5" />
            ) : (
              <MessageCircle className="!size-5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>Message all riders</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to={`${CONST.ROUTES.REGISTER_EVENT.URL}/${eventId}/${profileData.id}`}>
            <Button
              variant="outline"
              className="p-2 w-[42px] h-[42px] text-SeabiscuitDark200ThemeColor/50"
            >
              <PlusIcon className="!w-full !h-full" />
            </Button>
          </Link>
        </TooltipTrigger>
        <TooltipContent>Add new entry</TooltipContent>
      </Tooltip>
      {syncLoading && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" className="text-black/50 w-[45px] h-[42px] rounded-md">
              <RefreshCcw className="!size-5 animate-spin" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Synchronizing all data. Please wait, the table will be updated soon.
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}
